/** biome-ignore-all lint/performance/noImgElement: disabled */
/** biome-ignore-all lint/suspicious/noArrayIndexKey: disabled */
/** biome-ignore-all lint/correctness/useImageSize: disabled */

import { Button } from "@components";
import { ArrowLeft, ArrowRight } from "@untitledui/icons";
import useEmblaCarousel, {
	type UseEmblaCarouselType,
} from "embla-carousel-react";
import React, { useCallback, useEffect, useState } from "react";
import { cx } from "@/utils/cx";

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
	opts?: CarouselOptions;
	plugins?: CarouselPlugin;
	orientation?: "horizontal" | "vertical";
	setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
	carouselRef: ReturnType<typeof useEmblaCarousel>[0];
	api: ReturnType<typeof useEmblaCarousel>[1];
	scrollPrev: () => void;
	scrollNext: () => void;
	canScrollPrev: boolean;
	canScrollNext: boolean;
} & CarouselProps;

type PropType = {
	selected: boolean;
	index: number;
	onClick: () => void;
	img: string;
};

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
	const context = React.useContext(CarouselContext);

	if (!context) {
		throw new Error("useCarousel must be used within a <Carousel />");
	}

	return context;
}

function Carousel({
	orientation = "horizontal",
	opts,
	setApi,
	plugins,
	className,
	children,
	...props
}: React.ComponentProps<"div"> & CarouselProps) {
	const [carouselRef, api] = useEmblaCarousel(
		{
			...opts,
			axis: orientation === "horizontal" ? "x" : "y",
		},
		plugins
	);
	const [canScrollPrev, setCanScrollPrev] = React.useState(false);
	const [canScrollNext, setCanScrollNext] = React.useState(false);

	const onSelect = React.useCallback((apiProp: CarouselApi) => {
		if (!apiProp) {
			return;
		}
		setCanScrollPrev(apiProp.canScrollPrev());
		setCanScrollNext(apiProp.canScrollNext());
	}, []);

	const scrollPrev = React.useCallback(() => {
		api?.scrollPrev();
	}, [api]);

	const scrollNext = React.useCallback(() => {
		api?.scrollNext();
	}, [api]);

	const handleKeyDown = React.useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (event.key === "ArrowLeft") {
				event.preventDefault();
				scrollPrev();
			} else if (event.key === "ArrowRight") {
				event.preventDefault();
				scrollNext();
			}
		},
		[scrollPrev, scrollNext]
	);

	React.useEffect(() => {
		if (!(api && setApi)) {
			return;
		}
		setApi(api);
	}, [api, setApi]);

	React.useEffect(() => {
		if (!api) {
			return;
		}
		onSelect(api);
		api.on("reInit", onSelect);
		api.on("select", onSelect);

		return () => {
			api?.off("select", onSelect);
		};
	}, [api, onSelect]);

	return (
		<CarouselContext.Provider
			value={{
				carouselRef,
				api,
				opts,
				orientation:
					orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
				scrollPrev,
				scrollNext,
				canScrollPrev,
				canScrollNext,
			}}
		>
			<div
				aria-roledescription="carousel"
				className={cx("relative", className)}
				data-slot="carousel"
				onKeyDownCapture={handleKeyDown}
				role="region"
				{...props}
			>
				{children}
			</div>
		</CarouselContext.Provider>
	);
}

function CarouselContent({ className, ...props }: React.ComponentProps<"div">) {
	const { carouselRef, orientation } = useCarousel();

	return (
		<div
			className="overflow-hidden"
			data-slot="carousel-content"
			ref={carouselRef}
		>
			<div
				className={cx(
					"flex",
					orientation === "horizontal" ? "" : "-mt-4 flex-col",
					className
				)}
				{...props}
			/>
		</div>
	);
}

function CarouselItem({ className, ...props }: React.ComponentProps<"div">) {
	const { orientation } = useCarousel();

	return (
		<div
			aria-roledescription="slide"
			className={cx(
				"min-w-0 shrink-0 grow-0 basis-full",
				orientation === "horizontal" ? "" : "pt-4",
				className
			)}
			data-slot="carousel-item"
			role="group"
			{...props}
		/>
	);
}

function CarouselPrevious({
	className,
	variant = "outline",
	size = "icon",
	...props
}: React.ComponentProps<typeof Button>) {
	const { orientation, scrollPrev, canScrollPrev } = useCarousel();

	return (
		<Button
			className={cx(
				"absolute size-10 rounded-full bg-alpha-black/40!",
				orientation === "horizontal"
					? "-left-12 -translate-y-1/2 top-1/2"
					: "-top-12 -translate-x-1/2 left-1/2 rotate-90",
				!canScrollPrev &&
					"pointer-events-auto! cursor-not-allowed! bg-disabled/40!",
				className
			)}
			data-slot="carousel-previous"
			disabled={!canScrollPrev}
			onClick={scrollPrev}
			size={size}
			variant={variant}
			{...props}
		>
			<ArrowLeft
				className={cx(
					"size-6 text-fg-white",
					!canScrollPrev && "text-disabled"
				)}
			/>
			<span className="sr-only">Previous slide</span>
		</Button>
	);
}

function CarouselNext({
	className,
	variant = "outline",
	size = "icon",
	...props
}: React.ComponentProps<typeof Button>) {
	const { orientation, scrollNext, canScrollNext } = useCarousel();

	return (
		<Button
			className={cx(
				"absolute size-10 rounded-full bg-alpha-black/40!",
				orientation === "horizontal"
					? "-right-12 -translate-y-1/2 top-1/2"
					: "-bottom-12 -translate-x-1/2 left-1/2 rotate-90",
				!canScrollNext &&
					"pointer-events-auto! cursor-not-allowed! bg-disabled/40!",
				className
			)}
			data-slot="carousel-next"
			disabled={!canScrollNext}
			onClick={scrollNext}
			size={size}
			variant={variant}
			{...props}
		>
			<ArrowRight
				className={cx(
					"size-6 text-fg-white",
					!canScrollNext && "text-disabled"
				)}
			/>
			<span className="sr-only">Next slide</span>
		</Button>
	);
}
type CarouselWithThumbnailsProps = {
	fixed?: boolean;
	options: { fileUrl: string }[];
};

const CarouselWithThumbnails = ({
	fixed = false,
	options,
}: CarouselWithThumbnailsProps) => {
	const [api, setApi] = useState<CarouselApi>();
	const [current, setCurrent] = useState(0);

	const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
		containScroll: "keepSnaps",
		dragFree: true,
	});

	const onThumbClick = useCallback(
		(index: number) => {
			if (!(api && emblaThumbsApi)) {
				return;
			}
			api.scrollTo(index);
		},
		[api, emblaThumbsApi]
	);

	const onSelect = useCallback(() => {
		if (!(api && emblaThumbsApi)) {
			return;
		}
		setCurrent(api.selectedScrollSnap());
		emblaThumbsApi.scrollTo(api.selectedScrollSnap());
	}, [api, emblaThumbsApi]);

	useEffect(() => {
		if (!api) {
			return;
		}
		onSelect();

		api.on("select", onSelect).on("reInit", onSelect);
	}, [api, onSelect]);

	return (
		<div
			className={cx(
				"flex size-full flex-1 items-center justify-center bg-gray-900",
				!!fixed && "fixed inset-0 z-10"
			)}
		>
			<Carousel className="mx-auto h-auto w-full" setApi={setApi}>
				<CarouselContent className="duration-150">
					{options?.map((item, index) => (
						<CarouselItem key={index}>
							<img
								alt="car"
								className="h-auto max-h-dvh w-full max-w-dvw overflow-hidden object-contain"
								src={item.fileUrl}
							/>
						</CarouselItem>
					))}
				</CarouselContent>
				<CarouselPrevious className="-translate-y-1/2 top-1/2 left-6 border-0" />
				<CarouselNext className="-translate-y-1/2 top-1/2 right-6 border-0" />
			</Carousel>

			<div className="absolute bottom-9 max-w-full">
				<div className="overflow-hidden px-4" ref={emblaThumbsRef}>
					<div className="flex gap-2">
						{options?.map((item, index) => (
							<Thumb
								img={item.fileUrl}
								index={index}
								key={index}
								onClick={() => onThumbClick(index)}
								selected={index === current}
							/>
						))}
					</div>
				</div>
			</div>
		</div>
	);
};

const Thumb: React.FC<PropType> = (props) => {
	const { selected, index, onClick, img } = props;

	return (
		<div
			className={cx(
				"h-[66px] min-h-[66px] w-[50px] min-w-[50px] rounded-sm bg-active",
				!!selected && "border-2 border-blue-200"
			)}
			key={index}
		>
			<button
				className={cx(
					"h-full w-full touch-manipulation",
					!!selected && "border-2 border-gray-900"
				)}
				onClick={onClick}
				type="button"
			>
				<img
					alt="car"
					className="h-full w-full object-cover"
					height={284}
					src={img}
					width={100}
				/>
			</button>
		</div>
	);
};

export {
	type CarouselApi,
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselPrevious,
	CarouselNext,
	CarouselWithThumbnails,
	Thumb,
};
